import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    CaseFile,
    MutationResult,
    PagedResponse,
    QueryHookParams,
    QueryParams,
    QueryResult,
    SortParams,
    UpdateMutation,
} from "../models";
import { getRequestOptions } from "../helper-functions";
import { useAuth } from "../contexts";
import { Operation } from "rfc6902";

export interface IUseCaseFileQuery {
    listQuery: QueryResult<CaseFile[]>;
    uploadMutation: MutationResult<void, unknown, FormData>;
    updateMutation: UpdateMutation<CaseFile>;
}

interface UseCaseFileQueryParams extends QueryHookParams {
    caseId?: number | string;
    caseFileCategoryId?: number;
    procedureId?: number | string;
    tissueId?: number | string;
}

export const useCaseFileQuery = (params?: UseCaseFileQueryParams): IUseCaseFileQuery => {
    const caseFileId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const caseId = isNaN(Number(params?.caseId)) ? 0 : Number(params?.caseId);
    const caseFileCategoryId = isNaN(Number(params?.caseFileCategoryId)) ? 0 : Number(params?.caseFileCategoryId);
    const procedureId = isNaN(Number(params?.procedureId)) ? 0 : Number(params?.procedureId);
    const tissueId = isNaN(Number(params?.tissueId)) ? 0 : Number(params?.tissueId);
    const { isAuthorized } = useAuth();
    const { get, post, patch } = useAxios();
    const url = Endpoints.CaseFiles;

    const queryParamsWithArgs: QueryParams = {
        caseFileCategoryId: caseFileCategoryId ? `${caseFileCategoryId}` : "",
        caseId: caseId ? `${caseId}` : "",
        procedureId: procedureId ? `${procedureId}` : "",
        tissueId: tissueId ? `${tissueId}` : "",
    };

    const sortParams: SortParams = {
        orderBy: "createdOn",
        orderDesc: "false",
    };

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<CaseFile>>(
                getRequestOptions(
                    `${url}/case/${caseId}`,
                    config,
                    queryParamsWithArgs,
                    sortParams,
                    undefined,
                    false,
                    true
                )
            ).then((response) => response.items),
        queryKey: queryKeys.caseFiles.list(queryParamsWithArgs),
        placeholderData: [],
        enabled: isAuthorized && !!caseId,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const uploadMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, void>({ url: `${url}/upload`, data }),
        queryKey: queryKeys.formData.details(),
        keysToInvalidate: [queryKeys.caseFiles.lists()],
        successToast: "File uploaded successfully",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], CaseFile>({ url: `${url}/${caseFileId}`, data }),
        queryKey: queryKeys.caseFiles.detail(caseFileId),
        keysToInvalidate: [queryKeys.caseFiles.lists()],
        successToast: "File updated",
    });

    return useMemo<IUseCaseFileQuery>(
        () => ({
            listQuery,
            uploadMutation,
            updateMutation,
        }),
        [listQuery, updateMutation, uploadMutation]
    );
};
